import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { api } from '../lib/api';
import shellStyles from '../components/shell/shell.module.css';
import ui from '../styles/ui.module.css';

const TYPE_LABEL: Record<string, string> = {
  sale: 'Venda',
  commission: 'Comissão Storeo',
  reserve_held: 'Reserva retida',
  reserve_released: 'Reserva liberada',
  reversal: 'Estorno',
  chargeback: 'Chargeback',
};

function money(v: number): string {
  return v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

function when(iso: string): string {
  return new Date(iso).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' });
}

const SIZE = 20;

/** P15/P16 · Dinheiro — saldo, extrato paginado e taxa de chargeback (camada L4). */
export function Dinheiro() {
  const [page, setPage] = useState(0);
  const balance = useQuery({ queryKey: ['balance'], queryFn: () => api.balance() });
  const chargeback = useQuery({ queryKey: ['chargebackRate'], queryFn: () => api.chargebackRate() });
  const ledger = useQuery({ queryKey: ['ledger', page], queryFn: () => api.ledger({ page, size: SIZE }) });

  return (
    <>
      <div className={shellStyles.head}>
        <h1>Dinheiro</h1>
        <p>Seu saldo e o extrato de cada venda, comissão e ajuste.</p>
      </div>

      <section className={ui.stats}>
        <div className={ui.stat}>
          <span className={ui.statLbl}>Saldo disponível</span>
          <div className={ui.statVal}>
            <span className={ui.statCur}>R$</span>
            {balance.data ? balance.data.available.toLocaleString('pt-BR', { minimumFractionDigits: 2 }) : '—'}
          </div>
        </div>
        <div className={ui.stat}>
          <span className={ui.statLbl}>Chargeback (30 dias)</span>
          <div className={ui.statVal}>{chargeback.data ? `${chargeback.data.ratePct.toFixed(2)}%` : '—'}</div>
          <span className={ui.statSub}>
            {chargeback.data ? `${chargeback.data.cardChargebacks} de ${chargeback.data.cardPaid} no cartão` : ''}
          </span>
        </div>
      </section>

      <section>
        <div className={ui.secH}>
          <h2>Extrato</h2>
        </div>
        <div className={ui.card}>
          {!ledger.data?.items.length ? (
            <p className={ui.empty}>Nada ainda.</p>
          ) : (
            <div className={ui.tblScroll}>
              <table className={ui.tbl}>
                <thead>
                  <tr>
                    <th>Quando</th>
                    <th>Tipo</th>
                    <th className="r">Valor</th>
                    <th className="r">Saldo</th>
                  </tr>
                </thead>
                <tbody>
                  {ledger.data.items.map((l) => (
                    <tr key={l.id}>
                      <td className={ui.faint}>{when(l.createdAt)}</td>
                      <td className={ui.muted}>{TYPE_LABEL[l.type] ?? l.type}</td>
                      <td className="r">
                        <span className={ui.money} style={{ color: l.amount < 0 ? 'var(--storeo-danger)' : undefined }}>
                          {l.amount < 0 ? '−' : '+'} {money(Math.abs(l.amount))}
                        </span>
                      </td>
                      <td className="r">
                        <span className={ui.money}>{money(l.balanceAfter)}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </section>

      <div className={ui.actions}>
        <button
          className={`${ui.btn} ${ui.btnGhost} ${ui.btnSmall}`}
          disabled={page === 0}
          onClick={() => setPage((p) => Math.max(0, p - 1))}
        >
          ← Mais recentes
        </button>
        <button
          className={`${ui.btn} ${ui.btnGhost} ${ui.btnSmall}`}
          disabled={!ledger.data?.hasMore}
          onClick={() => setPage((p) => p + 1)}
        >
          Mais antigos →
        </button>
      </div>
    </>
  );
}
